import React, { useState } from 'react';
import { SessionData, AnalysisResult } from './types';
import { analyzeSession, generateSampleData } from './utils/analysis';
import { generatePDFReport } from './utils/pdfExport';
import { DataInput } from './components/DataInput';
import { FVChart } from './components/FVChart';
import { KPICards } from './components/KPICards';
import { TrainingZones } from './components/TrainingZones';
import { QualityCheck } from './components/QualityCheck';
import { PowerLoadChart } from './components/PowerLoadChart';
import { VelocityLossChart } from './components/VelocityLossChart';
import { PerformanceRadar } from './components/PerformanceRadar';
import { PowerTimeChart } from './components/PowerTimeChart';
import { ValidationChart } from './components/ValidationChart';
import { InjuryPreventionChart } from './components/InjuryPreventionChart';
import { TrainerInsights } from './components/TrainerInsights';

function App() {
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [activeTab, setActiveTab] = useState<'input' | 'analysis'>('input');

  const handleDataSubmit = (data: SessionData) => {
    setSessionData(data);
    const result = analyzeSession(data);
    setAnalysisResult(result);
    setActiveTab('analysis');
  };

  const loadSampleData = () => {
    const sampleData = generateSampleData();
    handleDataSubmit(sampleData);
  };

  const exportData = () => {
    if (sessionData && analysisResult) {
      const exportData = {
        sessionData,
        analysisResult,
        exportDate: new Date().toISOString()
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `t-apex-analysis-${sessionData.athlete.name}-${sessionData.session.date}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const exportPDF = () => {
    if (sessionData && analysisResult) {
      generatePDFReport(sessionData, analysisResult);
    }
  };

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Header */}
      <header className="bg-theme-surface-1 border-b border-theme-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img 
                src="/gamefit_logo.png" 
                alt="Gamefit Training Logo" 
                className="h-12 w-auto"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-theme-text">T-Apex Performance Analysis</h1>
                <p className="text-base leading-6 text-theme-muted">Force-Velocity Profiling & Sprint Analysis by <span className="font-medium">Gamefit Training</span></p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={loadSampleData}
                className="px-4 py-2 bg-theme-surface-2 text-theme-text hover:bg-slate-600 active:bg-slate-500 border border-theme-border rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-theme-focus"
              >
                Load Sample
              </button>
              {analysisResult && (
                <>
                  <button
                    onClick={exportData}
                    className="px-4 py-2 bg-green-600 text-white hover:bg-green-500 active:bg-green-700 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-theme-focus"
                  >
                    Export JSON
                  </button>
                  <button
                    onClick={exportPDF}
                    className="px-4 py-2 bg-theme-accent text-white hover:bg-theme-accent-2 active:bg-theme-accent-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-theme-focus"
                  >
                    Export PDF
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-6">
        <div className="border-b border-theme-border">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('input')}
              className={`py-2 px-1 border-b-2 font-medium text-base transition-colors ${
                activeTab === 'input'
                  ? 'border-theme-accent text-theme-text'
                  : 'border-transparent text-theme-muted hover:text-theme-text hover:border-slate-500'
              }`}
            >
              Data Input
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              disabled={!analysisResult}
              className={`py-2 px-1 border-b-2 font-medium text-base transition-colors ${
                activeTab === 'analysis' && analysisResult
                  ? 'border-theme-accent text-theme-text'
                  : 'border-transparent text-theme-muted hover:text-theme-text hover:border-slate-500'
              } ${!analysisResult ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Analysis Results
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {activeTab === 'input' && (
          <div className="max-w-5xl mx-auto">
            <DataInput onDataSubmit={handleDataSubmit} initialData={sessionData || undefined} />
          </div>
        )}

        {activeTab === 'analysis' && analysisResult && sessionData && (
          <div className="space-y-6">
            {/* Athlete Info */}
            <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
              <h2 className="text-2xl font-semibold tracking-tight text-theme-text mb-4">{sessionData.athlete.name}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-base text-theme-muted">
                <div><span className="font-medium text-theme-text">Mass:</span> {sessionData.athlete.massKg} kg</div>
                <div><span className="font-medium text-theme-text">Sport:</span> {sessionData.athlete.sport}</div>
                <div><span className="font-medium text-theme-text">Position:</span> {sessionData.athlete.position}</div>
                <div><span className="font-medium text-theme-text">Date:</span> {sessionData.session.date}</div>
              </div>
            </div>

            {/* KPI Cards */}
            <KPICards result={analysisResult} />

            {/* Charts and Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <FVChart 
                trials={sessionData.session.trials}
                fvProfile={analysisResult.fvProfile}
                vFreeMs={sessionData.session.v_free_ms}
              />
              <PowerLoadChart result={analysisResult} trials={sessionData.session.trials} />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <VelocityLossChart result={analysisResult} vFree={sessionData.session.v_free_ms} trials={sessionData.session.trials} />
              <PerformanceRadar result={analysisResult} />
            </div>
            
            {/* Enhanced Analysis Charts (if power/time data available) */}
            {(analysisResult.advancedProfiling || analysisResult.validation || analysisResult.injuryPrevention) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {analysisResult.advancedProfiling && (
                  <PowerTimeChart result={analysisResult} trials={sessionData.session.trials} />
                )}
                {analysisResult.validation && (
                  <ValidationChart result={analysisResult} />
                )}
                {analysisResult.injuryPrevention && (
                  <InjuryPreventionChart result={analysisResult} />
                )}
              </div>
            )}
            
            {/* Trainer Technical Guide */}
            {analysisResult.injuryPrevention && (
              <TrainerInsights result={analysisResult} />
            )}
            
            <TrainingZones result={analysisResult} />

            {/* Quality Check */}
            <QualityCheck quality={analysisResult.quality} />
          </div>
        )}
      </main>
    </div>
  );
}

export default App;